/* ═══════════════════════════════════════════════════
   SKIN ONE CLINICS — GSAP + ScrollTrigger Animations
   Performance: transforms only, staggered batches, kill on leave
   ═══════════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {
  initLoader();
  initScrollProgress();

  // Wait for GSAP + Lenis to load (deferred)
  const init = () => {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      return setTimeout(init, 50);
    }
    gsap.registerPlugin(ScrollTrigger);
    initLenis();
    initAnimations();
    initHeader();
    initMobileMenu();
    initAccordions();
    initBlogFilter();
    initParallax();
  };
  init();
});

/* ─── Loading Screen ─── */
function initLoader() {
  const loader = document.getElementById('loader');
  if (!loader) return;
  const logo = loader.querySelector('.loader__logo');
  const bar = loader.querySelector('.loader__bar');
  const fill = loader.querySelector('.loader__bar-fill');

  // Animate in
  requestAnimationFrame(() => {
    logo.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    logo.style.opacity = '1';
    logo.style.transform = 'translateY(0)';
    bar.style.transition = 'opacity 0.4s ease 0.2s';
    bar.style.opacity = '1';
    fill.style.width = '100%';
  });

  // Hide after load
  window.addEventListener('load', () => {
    setTimeout(() => { loader.classList.add('is-hidden'); }, 600);
  });

  // Fallback — never block more than 3s
  setTimeout(() => { loader.classList.add('is-hidden'); }, 3000);
}

/* ─── Scroll Progress Bar ─── */
function initScrollProgress() {
  const bar = document.getElementById('scroll-progress');
  if (!bar) return;
  window.addEventListener('scroll', () => {
    const h = document.documentElement.scrollHeight - window.innerHeight;
    bar.style.width = h > 0 ? (window.scrollY / h * 100) + '%' : '0%';
  }, { passive: true });
}

/* ─── Lenis Smooth Scroll — DISABLED (caused janky scroll) ─── */
function initLenis() {
  // Intentionally empty — using native browser scroll
}

/* ─── GSAP Scroll Animations ─── */
function initAnimations() {
  // Set initial state for animated elements (exclude accordion content)
  gsap.set('.gsap-fade-up:not(.service-accordion__content .gsap-fade-up)', { opacity: 0, y: 40 });
  gsap.set('.gsap-fade-left', { opacity: 0, x: -40 });
  gsap.set('.gsap-fade-right', { opacity: 0, x: 40 });
  gsap.set('.gsap-scale-in', { opacity: 0, scale: 0.9 });

  // Batch reveal — staggered fade up (cards, grid items)
  ScrollTrigger.batch('.gsap-fade-up', {
    onEnter: (batch) => {
      gsap.to(batch, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power2.out',
        overwrite: true
      });
    },
    start: 'top 88%',
    once: true
  });

  // Fade left
  ScrollTrigger.batch('.gsap-fade-left', {
    onEnter: (batch) => {
      gsap.to(batch, {
        opacity: 1,
        x: 0,
        duration: 0.9,
        stagger: 0.1,
        ease: 'power2.out',
        overwrite: true
      });
    },
    start: 'top 85%',
    once: true
  });

  // Fade right
  ScrollTrigger.batch('.gsap-fade-right', {
    onEnter: (batch) => {
      gsap.to(batch, {
        opacity: 1,
        x: 0,
        duration: 0.9,
        stagger: 0.1,
        ease: 'power2.out',
        overwrite: true
      });
    },
    start: 'top 85%',
    once: true
  });

  // Scale in (cards, images)
  ScrollTrigger.batch('.gsap-scale-in', {
    onEnter: (batch) => {
      gsap.to(batch, {
        opacity: 1,
        scale: 1,
        duration: 0.7,
        stagger: 0.08,
        ease: 'back.out(1.2)',
        overwrite: true
      });
    },
    start: 'top 85%',
    once: true
  });

  // Hero timeline — sequenced entrance
  const heroTl = gsap.timeline({ delay: 0.2 });
  const heroElements = document.querySelectorAll('.home-hero .gsap-fade-up');
  if (heroElements.length) {
    heroElements.forEach((el) => {
      const delay = parseFloat(el.dataset.delay) || 0;
      heroTl.to(el, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out'
      }, delay);
    });
  }

  // Section titles — clip reveal effect
  gsap.utils.toArray('.gsap-title-reveal').forEach(el => {
    gsap.from(el, {
      scrollTrigger: {
        trigger: el,
        start: 'top 85%',
        once: true
      },
      clipPath: 'inset(0 100% 0 0)',
      duration: 1,
      ease: 'power3.inOut'
    });
  });

  // Membership cards — staggered slide up with spring
  gsap.utils.toArray('.membership').forEach((card, i) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 90%',
        once: true
      },
      opacity: 0,
      y: 60,
      duration: 0.8,
      delay: i * 0.1,
      ease: 'power2.out'
    });
  });

  // Stats counter animation
  gsap.utils.toArray('.home-hero__stat-number').forEach(el => {
    const text = el.textContent;
    const num = parseInt(text);
    if (!isNaN(num) && num > 0) {
      const suffix = text.replace(/\d+/, '');
      el.textContent = '0' + suffix;
      ScrollTrigger.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: () => {
          gsap.to({ val: 0 }, {
            val: num,
            duration: 1.5,
            ease: 'power2.out',
            onUpdate: function() {
              el.textContent = Math.round(this.targets()[0].val) + suffix;
            }
          });
        }
      });
    }
  });

  // CRO: Testimonial cards — stagger with rotation
  gsap.utils.toArray('.testimonial-card').forEach((card, i) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 88%',
        once: true
      },
      opacity: 0,
      y: 40,
      rotation: i % 2 === 0 ? -2 : 2,
      duration: 0.8,
      delay: i * 0.15,
      ease: 'power2.out'
    });
  });

  // Value prop cards — stagger with slight scale
  gsap.utils.toArray('.value-card').forEach((card, i) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 88%',
        once: true
      },
      opacity: 0,
      y: 30,
      scale: 0.95,
      duration: 0.6,
      delay: i * 0.1,
      ease: 'power2.out'
    });
  });

  // Service cards — cascade effect
  gsap.utils.toArray('.service-card').forEach((card, i) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 90%',
        once: true
      },
      opacity: 0,
      y: 40,
      x: i % 2 === 0 ? -20 : 20,
      duration: 0.7,
      delay: i * 0.08,
      ease: 'power2.out'
    });
  });

  // Team cards — stagger reveal
  gsap.utils.toArray('.team-card').forEach((card, i) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: 'top 88%',
        once: true
      },
      opacity: 0,
      y: 50,
      duration: 0.7,
      delay: i * 0.1,
      ease: 'power2.out'
    });
  });

  // Memberships CTA — scrub parallax on the dark card
  const memCta = document.querySelector('.memberships-cta');
  if (memCta) {
    gsap.from(memCta, {
      scrollTrigger: {
        trigger: memCta,
        start: 'top 85%',
        once: true
      },
      opacity: 0,
      y: 60,
      scale: 0.96,
      duration: 1,
      ease: 'power3.out'
    });
  }
}

/* ─── Parallax Blobs & Images ─── */
function initParallax() {
  // Hero blobs — subtle parallax on scroll
  const blob1 = document.querySelector('.home-hero__blob-1');
  const blob2 = document.querySelector('.home-hero__blob-2');

  if (blob1) {
    gsap.to(blob1, {
      y: -80,
      scrollTrigger: {
        trigger: '.home-hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1.5
      }
    });
  }
  if (blob2) {
    gsap.to(blob2, {
      y: 60,
      x: 30,
      scrollTrigger: {
        trigger: '.home-hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1.5
      }
    });
  }

  // Hero image — subtle float
  const heroImg = document.querySelector('.home-hero__image');
  if (heroImg) {
    gsap.to(heroImg, {
      y: -20,
      scrollTrigger: {
        trigger: '.home-hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 2
      }
    });
  }

  // Section backgrounds — subtle color shift parallax
  gsap.utils.toArray('.section--cream, .section--white').forEach(section => {
    gsap.from(section, {
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'top center',
        scrub: 1
      },
      opacity: 0.7
    });
  });
}

/* ─── Header Scroll ─── */
function initHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  // GSAP-powered header transition
  ScrollTrigger.create({
    start: 50,
    onUpdate: (self) => {
      if (self.scroll() > 50) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }
  });
}

/* ─── Mobile Menu ─── */
function initMobileMenu() {
  const burger = document.querySelector('.site-header__burger');
  const mobileNav = document.querySelector('.mobile-nav');
  if (!burger || !mobileNav) return;

  burger.addEventListener('click', () => {
    const isOpen = mobileNav.classList.toggle('is-open');
    burger.setAttribute('aria-expanded', isOpen);
    const spans = burger.querySelectorAll('span');

    if (isOpen) {
      // GSAP burger animation
      gsap.to(spans[0], { rotation: 45, y: 7, duration: 0.3, ease: 'power2.inOut' });
      gsap.to(spans[1], { opacity: 0, duration: 0.2 });
      gsap.to(spans[2], { rotation: -45, y: -7, duration: 0.3, ease: 'power2.inOut' });

      // Slide in mobile nav items
      const items = mobileNav.querySelectorAll('a, div');
      gsap.from(items, {
        opacity: 0,
        x: -20,
        duration: 0.4,
        stagger: 0.05,
        ease: 'power2.out'
      });
    } else {
      gsap.to(spans[0], { rotation: 0, y: 0, duration: 0.3 });
      gsap.to(spans[1], { opacity: 1, duration: 0.2 });
      gsap.to(spans[2], { rotation: 0, y: 0, duration: 0.3 });
    }
  });

  // Close on link click
  mobileNav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', () => {
      mobileNav.classList.remove('is-open');
      burger.setAttribute('aria-expanded', 'false');
      const spans = burger.querySelectorAll('span');
      gsap.to(spans[0], { rotation: 0, y: 0, duration: 0.3 });
      gsap.to(spans[1], { opacity: 1, duration: 0.2 });
      gsap.to(spans[2], { rotation: 0, y: 0, duration: 0.3 });
    });
  });
}

/* ─── Service Accordions ─── */
function initAccordions() {
  document.querySelectorAll('.service-accordion__header').forEach(header => {
    header.addEventListener('click', () => {
      const content = header.nextElementSibling;
      const isOpen = header.classList.contains('is-open');

      // Close all
      document.querySelectorAll('.service-accordion__header.is-open').forEach(h => {
        h.classList.remove('is-open');
        h.nextElementSibling.classList.remove('is-open');
      });

      // Open current
      if (!isOpen) {
        header.classList.add('is-open');
        content.classList.add('is-open');

        // Ensure inner elements are visible and animate cards
        const fadeUps = content.querySelectorAll('.gsap-fade-up');
        fadeUps.forEach(el => { el.style.opacity = '1'; el.style.transform = 'none'; });

        const cards = content.querySelectorAll('.service-detail');
        if (cards.length) {
          gsap.fromTo(cards,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.5, stagger: 0.06, ease: 'power2.out', delay: 0.1 }
          );
        }
      }
    });
  });
}

/* ─── Blog Filter ─── */
function initBlogFilter() {
  const filterBtns = document.querySelectorAll('.blog-filter__btn');
  const blogCards = document.querySelectorAll('.blog-card-wrapper');
  if (!filterBtns.length) return;

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const cat = btn.dataset.category;
      filterBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      blogCards.forEach(card => {
        if (cat === 'todos' || card.dataset.category === cat) {
          gsap.to(card, { opacity: 1, scale: 1, display: '', duration: 0.4, ease: 'power2.out' });
        } else {
          gsap.to(card, { opacity: 0, scale: 0.95, duration: 0.3, ease: 'power2.in', onComplete: () => card.style.display = 'none' });
        }
      });
    });
  });
}
